/**
 * WordPress dependencies
 */
import { useEffect } from '@safe-wordpress/element';
import { useSelect, useDispatch } from '@safe-wordpress/data';

/**
 * External dependencies
 */
import { store as NAB_DATA } from '@nab/data';
import type {
	AlternativeIndex,
	ExperimentId,
	ExperimentTypeName,
} from '@nab/types';

/**
 * Internal dependencies
 */
import { StatusManager } from '../status-manager';
import { store as NAB_HEATMAP } from '../../store';
import { useActiveAlternative, useActiveExperiment } from '../hooks';

export type ResultsProviderProps = {
	readonly experimentId: ExperimentId;
	readonly experimentType: ExperimentTypeName;
	readonly isPublicView: boolean;
	readonly isReadOnlyActive: boolean;
	readonly alternativeIndex: number;
	readonly firstDayOfWeek: number;
	readonly children: JSX.Element | JSX.Element[];
};

export const ResultsProvider = (
	props: ResultsProviderProps
): JSX.Element | null => {
	const { experimentId } = props;
	const experiment = useSelect(
		( select ) => select( NAB_DATA ).getExperiment( experimentId ),
		[ experimentId ]
	);
	const alternative = useSelect(
		( select ) => select( NAB_HEATMAP ).getActiveAlternative(),
		[]
	);
	useInitEffect( props );
	useResultLoader( experimentId, alternative );
	useResultProcessor();

	if ( ! experiment ) {
		return null;
	}

	const { children } = props;
	return (
		<div>
			<StatusManager />
			{ children }
		</div>
	);
};

// =====
// HOOKS
// =====

const useInitEffect = ( props: ResultsProviderProps ) => {
	const { setPageAttribute } = useDispatch( NAB_DATA );
	const { setActiveAlternative, setFirstDayOfWeek } =
		useDispatch( NAB_HEATMAP );
	useSelect(
		( select ) =>
			select( NAB_DATA ).getExperimentResults( props.experimentId ),
		[ props.experimentId ]
	);

	const {
		experimentId,
		experimentType,
		alternativeIndex,
		firstDayOfWeek,
		isPublicView,
		isReadOnlyActive,
	} = props;

	useEffect( () => {
		void setPageAttribute( 'editor/activeExperiment', experimentId );
		void setPageAttribute( 'editor/activeExperimentType', experimentType );
		void setPageAttribute( 'results/isPublicView', isPublicView );
		void setPageAttribute( 'results/isReadOnlyActive', isReadOnlyActive );
		void setActiveAlternative( alternativeIndex );
		void setFirstDayOfWeek( firstDayOfWeek % 7 );
	}, [
		experimentId,
		experimentType,
		alternativeIndex,
		firstDayOfWeek,
		isPublicView,
		isReadOnlyActive,
		setPageAttribute,
		setActiveAlternative,
		setFirstDayOfWeek,
	] );
};

const useResultLoader = (
	experimentId: ExperimentId,
	alternative: AlternativeIndex
) => {
	const experiment = useSelect(
		( select ) => select( NAB_DATA ).getExperiment( experimentId ),
		[ experimentId ]
	);
	const isExperimentLoading = useSelect(
		( select ) =>
			! select( NAB_DATA ).hasFinishedResolution( 'getExperiment', [
				experimentId,
			] ) &&
			! select( NAB_DATA ).hasResolutionFailed( 'getExperiment', [
				experimentId,
			] ),
		[ experimentId ]
	);
	const { loadResults, setRawResultStatus } = useDispatch( NAB_HEATMAP );

	useEffect( () => {
		if ( isExperimentLoading ) {
			return;
		}
		if ( experiment ) {
			void loadResults( experiment, alternative );
		} else {
			void setRawResultStatus( alternative, {
				mode: 'error',
				rationale: 'experiment-not-found',
			} );
		}
	}, [
		experiment,
		alternative,
		isExperimentLoading,
		loadResults,
		setRawResultStatus,
	] );
};

const useResultProcessor = () => {
	const { requestResultProcessing } = useDispatch( NAB_HEATMAP );
	const experiment = useActiveExperiment();
	const [ alternative ] = useActiveAlternative();
	const resolution = useSelect(
		( select ) => select( NAB_HEATMAP ).getActiveResolution(),
		[]
	);
	const { clicks, scrolls, processedStatus, rawStatus } = useSelect(
		( select ) => ( {
			mode: select( NAB_HEATMAP ).getActiveMode(),
			clicks: select( NAB_HEATMAP ).getRawClicks( alternative ),
			scrolls: select( NAB_HEATMAP ).getRawScrolls( alternative ),
			processedStatus: select( NAB_HEATMAP ).getProcessedResultStatus(
				alternative,
				resolution
			),
			rawStatus:
				select( NAB_HEATMAP ).getRawResultStatus( alternative )?.mode,
		} ),
		[ alternative, resolution ]
	);

	useEffect( () => {
		if ( ! experiment ) {
			return;
		}

		if (
			rawStatus === 'missing' ||
			rawStatus === 'loading' ||
			rawStatus === 'error'
		) {
			return;
		}

		if ( processedStatus === 'ready' || processedStatus === 'processing' ) {
			return;
		}

		void requestResultProcessing( alternative, resolution );
	}, [
		experiment,
		alternative,
		resolution,
		clicks,
		scrolls,
		processedStatus,
		rawStatus,
		requestResultProcessing,
	] );
};
